import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Crown, Zap, CheckCircle, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionModal = ({ onClose }) => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/user/upgrade-subscription`,
        { payment_method: 'demo_payment', payment_id: 'demo_' + Date.now() },
        { headers: getAuthHeader() }
      );
      toast.success(response.data.message);
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      toast.error('Failed to upgrade subscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="subscription-modal">
      <div className="bg-white dark:bg-[#2D2A26] rounded-xl border-2 border-black dark:border-white hard-shadow-lg max-w-2xl w-full p-8 relative">
        <button
          onClick={onClose}
          data-testid="close-subscription-modal"
          className="absolute top-4 right-4 p-2 hover:bg-[#F0F0F0] dark:hover:bg-[#3D3A36] rounded-full transition-all"
        >
          <X className="w-5 h-5 dark:text-white" strokeWidth={2.5} />
        </button>

        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Crown className="w-12 h-12 text-[#FFE66D]" strokeWidth={2.5} />
            <h2 className="text-5xl font-bold dark:text-white" style={{ fontFamily: 'Merriweather, serif' }}>
              Go Premium
            </h2>
          </div>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            Unlock unlimited wit for just $1/month
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-[#F0F0F0] dark:bg-[#3D3A36] border-2 border-black dark:border-gray-600 rounded-xl p-6">
            <h3 className="text-2xl font-bold mb-4 dark:text-white" style={{ fontFamily: 'Merriweather, serif' }}>
              Free Plan
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#4ECDC4]" strokeWidth={2.5} />
                <span className="dark:text-white">3 articles per day</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#4ECDC4]" strokeWidth={2.5} />
                <span className="dark:text-white">All categories</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#4ECDC4]" strokeWidth={2.5} />
                <span className="dark:text-white">Reactions & comments</span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#FFE66D] to-[#FF6B6B] border-2 border-black rounded-xl p-6 relative">
            <div className="absolute -top-3 -right-3 bg-[#FF6B6B] text-white px-4 py-1 rounded-full border-2 border-black font-bold text-sm">
              BEST VALUE
            </div>
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
              Premium
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5" strokeWidth={2.5} />
                <span className="font-bold">UNLIMITED articles</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" strokeWidth={2.5} />
                <span>All categories</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" strokeWidth={2.5} />
                <span>Reactions & comments</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" strokeWidth={2.5} />
                <span>No daily limits</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" strokeWidth={2.5} />
                <span>Cancel anytime</span>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <p className="text-3xl font-bold mb-4 dark:text-white" style={{ fontFamily: 'Merriweather, serif' }}>
            $1.00 <span className="text-lg font-normal text-gray-600 dark:text-gray-400">/ month</span>
          </p>
          <button
            onClick={handleUpgrade}
            disabled={loading}
            data-testid="upgrade-button"
            className="bg-[#FF6B6B] text-white font-bold px-12 py-4 rounded-full border-2 border-black text-xl hard-shadow-hover disabled:opacity-50 flex items-center gap-2 mx-auto"
          >
            {loading ? 'Processing...' : (
              <>
                <Crown className="w-6 h-6" strokeWidth={2.5} />
                Upgrade to Premium
              </>
            )}
          </button>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-4">
            Demo mode - No actual payment required
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;